// WS-MODULE-SCOPED-KEYS-CONTRACT-1 — the MODULE half of the tenant-boundary
// wipe registry.
//
// `scopes.js workspaceScopedWipe()` is the single source of truth for SHELL
// state that resets on an org/workspace switch (WORKSPACE_SCOPED_KEYS). A
// workspace MODULE (the takeover `settings.workspaceModule` or an additive
// `settings.workspaceApps[]` entry) writes its own root keys — and until this
// registry existed it had NO way to say "these are mine and tenant-scoped".
// Every module key survived the switch by construction unless the module
// wiped by hand at its own teardown (bellforge-app 0097ee9 / b1ec6eb —
// BF-WSEVENTS-SURVIVES-ORG-SWITCH-1, BF-MODULE-SLICES-DONT-RELOAD-ON-ORG-
// RETURN-1), and a module that forgets leaks one tenant's rows into the next.
//
// THE CONTRACT (manifest v2, read by workspaceProjects.js `_normalizeManifest`):
//
//   scopedKeys: ['bfWsEvents', 'bf*', { key: 'bfView', empty: 'home' }]
//   hooks: { onWorkspaceSwitch: 'bfReleaseTenantState' }
//
// • `scopedKeys` entries: a plain key, a `prefix*` family (every LIVE root key
//   that starts with the prefix), or `{ key | prefix, empty }` with an explicit
//   reset value. The default reset is the TYPED EMPTY of the live value
//   (array → [], boolean → false, number → 0, string → '', object → null) —
//   mirroring the shell's own `_freshEmpty` semantics (a hardcoded [] is
//   wrong-typed for an object key: truthy to `if (!key)`, see
//   WS-SWITCH-RESET-SHAPE). Absent keys are skipped — a wipe resets what
//   exists, it never mints state. Declared keys reset REGARDLESS of the
//   loader's "only seed when empty" gate: a declaration means "mine".
// • `hooks.onWorkspaceSwitch` names a MODULE function the shell calls at the
//   module's tenant boundary (first thing in `_teardownModule`, while the
//   module's functions still resolve and its surfaces are still mounted) as
//   `fn.call(rootElement, rootState, { reason, moduleId })`. It is the place
//   for late-write fences (a listing in flight at the boundary must drop its
//   result instead of repainting the outgoing tenant's rows).
//
// WHO CALLS WHAT. The loader registers on inject (takeover module and every
// additive-app ledger alike) and unregisters on teardown. `scopes.js
// workspaceScopedWipe(root)` spreads `moduleScopedWipe(liveState)` into the
// PHASE-1 `root.replace` of the switch pipeline, so every loaded module's
// declared keys reset SYNCHRONOUSLY at the boundary with no module code;
// `_clearState` / `_teardownApp` apply the same patch at teardown (idempotent:
// a second pass over already-empty keys writes the same empties).
//
// This file has NO shell imports on purpose — scopes.js imports it and
// workspaceProjects.js imports it; a dependency on either would be a cycle.

const _registry = new Map() // ownerId → { keys: NormalizedEntry[], hook: string|null }

// Typed empty of a LIVE value — the module's declared key keeps its shape.
export const typedEmpty = (v) => {
  if (Array.isArray(v)) return []
  if (typeof v === 'boolean') return false
  if (typeof v === 'number') return 0
  if (typeof v === 'string') return ''
  return null
}

// A fresh instance of an explicit `empty` — arrays/objects must be NEW per
// switch so one tenant's mutations cannot bleed into the next wipe.
const _freshOf = (v) => {
  if (Array.isArray(v)) return [...v]
  if (v && typeof v === 'object') return { ...v }
  return v
}

// Normalize one manifest entry → { key } | { prefix } (+ optional `empty`,
// flagged `hasEmpty` so an explicit `empty: null` / `empty: 0` is honoured).
// Malformed entries return null — the loader warns and skips, never throws.
export const normalizeScopedKeyEntry = (entry) => {
  if (typeof entry === 'string') {
    const s = entry.trim()
    if (!s) return null
    if (s.endsWith('*')) {
      const prefix = s.slice(0, -1)
      return prefix ? { prefix } : null
    }
    return { key: s }
  }
  if (entry && typeof entry === 'object') {
    const hasEmpty = Object.prototype.hasOwnProperty.call(entry, 'empty')
    if (typeof entry.prefix === 'string' && entry.prefix) {
      return hasEmpty
        ? { prefix: entry.prefix, empty: entry.empty, hasEmpty }
        : { prefix: entry.prefix }
    }
    if (typeof entry.key === 'string' && entry.key) {
      const key = entry.key.endsWith('*') ? null : entry.key
      if (!key)
        return hasEmpty
          ? { prefix: entry.key.slice(0, -1), empty: entry.empty, hasEmpty }
          : { prefix: entry.key.slice(0, -1) }
      return hasEmpty ? { key, empty: entry.empty, hasEmpty } : { key }
    }
  }
  return null
}

export const normalizeScopedKeys = (list) => {
  const out = []
  for (const raw of Array.isArray(list) ? list : []) {
    const n = normalizeScopedKeyEntry(raw)
    if (n) out.push(n)
  }
  return out
}

// Register (or replace) one owner's declaration. `keys` may be the raw
// manifest list or an already-normalized one. `hook` is the module function
// NAME (string) or null.
export const registerModuleScopedKeys = (ownerId, { keys = [], hook = null } = {}) => {
  if (!ownerId) return
  _registry.set(String(ownerId), {
    keys: normalizeScopedKeys(keys),
    hook: typeof hook === 'string' && hook ? hook : null
  })
}

export const unregisterModuleScopedKeys = (ownerId) => {
  if (ownerId == null) return
  _registry.delete(String(ownerId))
}

export const hasModuleScopedKeys = (ownerId) => _registry.has(String(ownerId))

// Read-only view for tests / the census: [{ ownerId, keys, hook }].
export const listModuleScopedDeclarations = () =>
  [..._registry.entries()].map(([ownerId, d]) => ({
    ownerId,
    keys: d.keys.map((e) => ({ ...e })),
    hook: d.hook
  }))

export const __resetModuleScopedKeysForTests = () => _registry.clear()

// Does a declaration (normalized entries) cover `key`? Prefix-aware. Used by
// the wipe and by the module root-write census.
export const scopedKeyEntriesCover = (entries, key) => {
  for (const e of entries || []) {
    if (e.key !== undefined && e.key === key) return true
    if (e.prefix !== undefined && key.startsWith(e.prefix)) return true
  }
  return false
}

// The live root state as a plain bag: smbls state exposes `parse()`; tests
// and the loader's `_clearState` path hand a plain object.
export const liveStateBag = (root) => {
  if (!root) return {}
  if (typeof root.parse === 'function') {
    try {
      const p = root.parse()
      if (p && typeof p === 'object') return p
    } catch (_) {
      /* fall through to own props */
    }
  }
  return root
}

// The wipe patch for ONE declaration over a live state bag.
export const wipePatchFor = (entries, bag) => {
  const patch = {}
  const live = bag || {}
  for (const e of entries || []) {
    if (e.key !== undefined) {
      if (!Object.prototype.hasOwnProperty.call(live, e.key)) continue
      patch[e.key] = e.hasEmpty ? _freshOf(e.empty) : typedEmpty(live[e.key])
      continue
    }
    if (e.prefix !== undefined) {
      for (const k of Object.keys(live)) {
        if (!k.startsWith(e.prefix)) continue
        // An explicit-key entry for the same key wins over a family default
        // (declared more specifically) — keep the first write.
        if (Object.prototype.hasOwnProperty.call(patch, k)) continue
        patch[k] = e.hasEmpty ? _freshOf(e.empty) : typedEmpty(live[k])
      }
    }
  }
  return patch
}

// The wipe patch for ONE owner (the loader's teardown path).
export const moduleScopedWipeFor = (ownerId, root) => {
  const d = ownerId != null ? _registry.get(String(ownerId)) : null
  if (!d || !d.keys.length) return {}
  return wipePatchFor(d.keys, liveStateBag(root))
}

// The wipe patch over EVERY registered module, for the PHASE-1 spread. Takes
// the root state (or a plain bag) — a prefix family needs the live key list.
export const moduleScopedWipe = (root) => {
  if (!_registry.size) return {}
  const bag = liveStateBag(root)
  const patch = {}
  // Explicit keys first across all owners, then families, so a specific
  // declaration always wins over a prefix default from any owner.
  const explicit = []
  const families = []
  for (const d of _registry.values()) {
    for (const e of d.keys) (e.key !== undefined ? explicit : families).push(e)
  }
  Object.assign(patch, wipePatchFor(explicit, bag))
  for (const [k, v] of Object.entries(wipePatchFor(families, bag))) {
    if (!Object.prototype.hasOwnProperty.call(patch, k)) patch[k] = v
  }
  return patch
}

// Run one owner's `onWorkspaceSwitch` hook. `resolveFn(name)` returns the
// module function (the loader passes a ctx.functions lookup); `thisArg` is the
// root element when one exists. Never throws — a module hook that fails must
// not abort the switch; the failure is logged with the module named.
export const runModuleSwitchHook = (
  ownerId,
  { resolveFn, thisArg, root, reason = 'teardown' } = {}
) => {
  const d = _registry.get(String(ownerId))
  if (!d || !d.hook) return false
  const fn = typeof resolveFn === 'function' ? resolveFn(d.hook) : null
  if (typeof fn !== 'function') {
    console.warn(
      '[workspaceModule] onWorkspaceSwitch hook is not a module function:',
      ownerId,
      d.hook
    )
    return false
  }
  try {
    fn.call(thisArg || root, root, { reason, moduleId: String(ownerId) })
    return true
  } catch (err) {
    console.warn(
      '[workspaceModule] onWorkspaceSwitch hook threw:',
      ownerId,
      d.hook,
      err && err.message
    )
    return false
  }
}
